/**
 * Circle of Fifths Wheel v1 (Phase 3 Tier 3) — pure-function core.
 *
 * Two sub-modes: Wheel Explore (free-explore, key-info + closely-related
 * cluster) and Key Relationship Quiz (10-Q session, two question kinds:
 * find-relative + find-closely-related). All key data — pool, signature
 * deltas, closely-related cluster math — is Bowie-locked verbatim from
 * the scope memo.
 *
 * Spec: docs/superpowers/specs/2026-05-23-cof-wheel-v1-design.md §4
 * Bowie scope memo: .claude/agent-memory/bowie/project_cof_wheel_v1_scope.md
 */

export type CanonicalMajor =
  | 'C'
  | 'G'
  | 'D'
  | 'A'
  | 'E'
  | 'B'
  | 'F#'
  | 'C#'
  | 'G#'
  | 'D#'
  | 'A#'
  | 'F'

export interface KeySlot {
  canonical: CanonicalMajor
  position: number
  majorLabels: readonly string[]
  relativeMinor: string
  signatureDelta: number
}

export const KEY_POOL: readonly KeySlot[] = [
  { canonical: 'C', position: 0, majorLabels: ['C'], relativeMinor: 'A', signatureDelta: 0 },
  { canonical: 'G', position: 1, majorLabels: ['G'], relativeMinor: 'E', signatureDelta: 1 },
  { canonical: 'D', position: 2, majorLabels: ['D'], relativeMinor: 'B', signatureDelta: 2 },
  { canonical: 'A', position: 3, majorLabels: ['A'], relativeMinor: 'F#', signatureDelta: 3 },
  { canonical: 'E', position: 4, majorLabels: ['E'], relativeMinor: 'C#', signatureDelta: 4 },
  { canonical: 'B', position: 5, majorLabels: ['B', 'Cb'], relativeMinor: 'G#', signatureDelta: 5 },
  { canonical: 'F#', position: 6, majorLabels: ['F#', 'Gb'], relativeMinor: 'D#', signatureDelta: 6 },
  { canonical: 'C#', position: 7, majorLabels: ['C#', 'Db'], relativeMinor: 'A#', signatureDelta: 7 },
  { canonical: 'G#', position: 8, majorLabels: ['Ab'], relativeMinor: 'F', signatureDelta: -4 },
  { canonical: 'D#', position: 9, majorLabels: ['Eb'], relativeMinor: 'C', signatureDelta: -3 },
  { canonical: 'A#', position: 10, majorLabels: ['Bb'], relativeMinor: 'G', signatureDelta: -2 },
  { canonical: 'F', position: 11, majorLabels: ['F'], relativeMinor: 'D', signatureDelta: -1 },
]

/** Enharmonic minor-key spelling pairs. */
const MINOR_ENHARMONICS: Record<string, string> = {
  'D#': 'Eb',
  Eb: 'D#',
  'G#': 'Ab',
  Ab: 'G#',
  'A#': 'Bb',
  Bb: 'A#',
}

/** Enharmonic major-key spelling pairs. */
const MAJOR_ENHARMONICS: Record<string, string> = {
  'F#': 'Gb',
  Gb: 'F#',
  'C#': 'Db',
  Db: 'C#',
  B: 'Cb',
  Cb: 'B',
  Ab: 'G#',
  'G#': 'Ab',
  Eb: 'D#',
  'D#': 'Eb',
  Bb: 'A#',
  'A#': 'Bb',
}

const expandMajorSpellings = (name: string): readonly string[] => {
  const alt = MAJOR_ENHARMONICS[name]
  return alt ? [name, alt] : [name]
}

const expandMinorSpellings = (name: string): readonly string[] => {
  const alt = MINOR_ENHARMONICS[name]
  return alt ? [`${name}m`, `${alt}m`] : [`${name}m`]
}

const findSlotByMajorName = (name: string): KeySlot | undefined =>
  KEY_POOL.find(slot => slot.majorLabels.includes(name) || slot.canonical === name)

export const getCloselyRelatedKeys = (majorKey: string): readonly string[] => {
  const slot = findSlotByMajorName(majorKey)
  if (!slot) {
    return []
  }
  const p = slot.position
  const ivMaj = KEY_POOL[(p + 11) % 12].canonical
  const vMaj = KEY_POOL[(p + 1) % 12].canonical
  const viMin = `${slot.relativeMinor}m`
  const iiMin = `${KEY_POOL[(p + 11) % 12].relativeMinor}m`
  const iiiMin = `${KEY_POOL[(p + 1) % 12].relativeMinor}m`
  return [ivMaj, vMaj, viMin, iiMin, iiiMin]
}

export const getRelativeKey = (
  key: string,
  ring: 'major' | 'minor'
): { name: string; ring: 'major' | 'minor' } => {
  if (ring === 'major') {
    const slot = findSlotByMajorName(key)
    if (!slot) {
      return { name: key, ring: 'minor' }
    }
    return { name: slot.relativeMinor, ring: 'minor' }
  }
  // minor ring → find the slot whose relativeMinor matches (or matches an enharmonic spelling).
  const alt = MINOR_ENHARMONICS[key]
  const slot = KEY_POOL.find(s => s.relativeMinor === key || (alt !== undefined && s.relativeMinor === alt))
  if (!slot) {
    return { name: key, ring: 'major' }
  }
  return { name: slot.canonical, ring: 'major' }
}

// ─── v1.5 helpers ───

/** Parallel key = same tonic, opposite mode. Spec §4.1. */
export const getParallelKey = (
  name: string,
  ring: 'major' | 'minor'
): { name: string; ring: 'major' | 'minor' } => ({
  name,
  ring: ring === 'major' ? 'minor' : 'major',
})

/** Enharmonic alternate signature deltas for positions 5/6/7. Spec §4.2. */
const ENHARMONIC_ALT_DELTA: Record<number, number> = {
  5: -7, // B (+5) ↔ Cb (-7)
  6: -6, // F# (+6) ↔ Gb (-6)
  7: -5, // C# (+7) ↔ Db (-5)
}

/** Enharmonic-pair display labels by position. Index 0 = sharp, 1 = flat. */
const ENHARMONIC_MAJOR_PAIR: Record<number, readonly [string, string]> = {
  5: ['B', 'Cb'],
  6: ['F#', 'Gb'],
  7: ['C#', 'Db'],
}
const ENHARMONIC_MINOR_PAIR: Record<number, readonly [string, string]> = {
  5: ['G#m', 'Abm'],
  6: ['D#m', 'Ebm'],
  7: ['A#m', 'Bbm'],
}

/**
 * Returns the requested-direction spelling for the slot's key, or `null` if
 * the slot doesn't have a genuine enharmonic alternate (positions 5/6/7 only).
 *
 * Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §4.2.
 */
export const getEnharmonicSpelling = (
  slot: KeySlot,
  ring: 'major' | 'minor',
  direction: 'sharp' | 'flat'
): string | null => {
  const pair = ring === 'major' ? ENHARMONIC_MAJOR_PAIR[slot.position] : ENHARMONIC_MINOR_PAIR[slot.position]
  if (!pair) {
    return null
  }
  return direction === 'sharp' ? pair[0] : pair[1]
}

/**
 * Number of accidentals in the slot's key signature for the requested direction,
 * or `null` if the slot doesn't support that direction. Returns 0 for both
 * directions when `signatureDelta === 0` (zero-equivalence at C major / A minor).
 *
 * Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §4.2 / §4.4.
 */
export const getKeySignatureCount = (
  slot: KeySlot,
  direction: 'sharp' | 'flat'
): number | null => {
  if (slot.signatureDelta === 0) {
    return 0
  }
  if (slot.signatureDelta > 0 && direction === 'sharp') {
    return slot.signatureDelta
  }
  if (slot.signatureDelta < 0 && direction === 'flat') {
    return -slot.signatureDelta
  }
  const alt = ENHARMONIC_ALT_DELTA[slot.position]
  if (alt === undefined) {
    return null
  }
  return Math.abs(alt)
}

// ─── Quiz machinery ───

export type CofQuizQuestionKind =
  | 'find-relative'
  | 'find-closely-related'
  | 'find-parallel'
  | 'count-signature'
  | 'spell-enharmonic'

export type CofQuizSubMode = 'mixed' | 'relationships' | 'signatures'

export type CofQuizPicked =
  | { kind: 'key'; name: string; ring: 'major' | 'minor' }
  | { kind: 'signature'; count: number; direction: 'sharp' | 'flat' }

interface CofQuizQuestionBase {
  referenceKey: { name: string; ring: 'major' | 'minor' }
  promptLabel: string
}

export type CofQuizQuestion =
  | (CofQuizQuestionBase & {
      kind: 'find-relative' | 'find-closely-related' | 'find-parallel'
      acceptedAnswers: readonly string[]
    })
  | (CofQuizQuestionBase & {
      kind: 'count-signature'
      promptDirection: 'sharp' | 'flat'
      acceptedCount: number
    })
  | (CofQuizQuestionBase & {
      kind: 'spell-enharmonic'
      promptDirection: 'sharp' | 'flat'
      acceptedSpellings: readonly string[]
    })

const expandClusterSpellings = (cluster: readonly string[]): readonly string[] => {
  const out = new Set<string>()
  cluster.forEach(name => {
    if (name.endsWith('m')) {
      expandMinorSpellings(name.replace(/m$/, '')).forEach(n => out.add(n))
    } else {
      expandMajorSpellings(name).forEach(n => out.add(n))
    }
  })
  return [...out]
}

const ALLOWED_KINDS: Record<CofQuizSubMode, readonly CofQuizQuestionKind[]> = {
  mixed: ['find-relative', 'find-closely-related', 'find-parallel', 'count-signature', 'spell-enharmonic'],
  relationships: ['find-relative', 'find-closely-related', 'find-parallel'],
  signatures: ['count-signature', 'spell-enharmonic'],
}

const pickKindByHistory = (
  allowed: readonly CofQuizQuestionKind[],
  history: readonly CofQuizQuestionKind[],
  rng: () => number
): CofQuizQuestionKind => {
  const counts = allowed.map(k => history.filter(h => h === k).length)
  const minCount = Math.min(...counts)
  const candidates = allowed.filter((_, i) => counts[i] === minCount)
  return candidates[Math.min(candidates.length - 1, Math.floor(rng() * candidates.length))]
}

const pickSlot = (rng: () => number): KeySlot =>
  KEY_POOL[Math.min(KEY_POOL.length - 1, Math.floor(rng() * KEY_POOL.length))]

const ENHARMONIC_SLOT_POSITIONS = [5, 6, 7] as const
const pickEnharmonicSlot = (rng: () => number): KeySlot => {
  const pos =
    ENHARMONIC_SLOT_POSITIONS[
      Math.min(ENHARMONIC_SLOT_POSITIONS.length - 1, Math.floor(rng() * ENHARMONIC_SLOT_POSITIONS.length))
    ]
  return KEY_POOL[pos]
}

/**
 * Generates a CoF quiz question. Sub-mode filters allowed kinds; `history`
 * enables round-robin bias (lowest-count rule). Spec §4.3.
 */
export const generateCofQuizQuestion = (
  subMode: CofQuizSubMode = 'mixed',
  rng: () => number = Math.random,
  history: readonly CofQuizQuestionKind[] = []
): CofQuizQuestion => {
  const allowed = ALLOWED_KINDS[subMode]
  const kind = pickKindByHistory(allowed, history, rng)

  if (kind === 'find-relative') {
    const slot = pickSlot(rng)
    const ring: 'major' | 'minor' = rng() < 0.5 ? 'major' : 'minor'
    if (ring === 'major') {
      return {
        kind,
        referenceKey: { name: slot.canonical, ring: 'major' },
        acceptedAnswers: expandMinorSpellings(slot.relativeMinor),
        promptLabel: `${slot.canonical} major`,
      }
    }
    return {
      kind,
      referenceKey: { name: slot.relativeMinor, ring: 'minor' },
      acceptedAnswers: expandMajorSpellings(slot.canonical),
      promptLabel: `${slot.relativeMinor} minor`,
    }
  }

  if (kind === 'find-closely-related') {
    const slot = pickSlot(rng)
    const ring: 'major' | 'minor' = rng() < 0.5 ? 'major' : 'minor'
    const cluster = getCloselyRelatedKeys(slot.canonical)
    const refName = ring === 'major' ? slot.canonical : slot.relativeMinor
    return {
      kind,
      referenceKey: { name: refName, ring },
      acceptedAnswers: expandClusterSpellings(cluster),
      promptLabel: `${refName} ${ring}`,
    }
  }

  if (kind === 'find-parallel') {
    const slot = pickSlot(rng)
    const ring: 'major' | 'minor' = rng() < 0.5 ? 'major' : 'minor'
    if (ring === 'major') {
      return {
        kind,
        referenceKey: { name: slot.canonical, ring: 'major' },
        acceptedAnswers: expandMinorSpellings(slot.canonical),
        promptLabel: `${slot.canonical} major`,
      }
    }
    return {
      kind,
      referenceKey: { name: slot.relativeMinor, ring: 'minor' },
      acceptedAnswers: expandMajorSpellings(slot.relativeMinor),
      promptLabel: `${slot.relativeMinor} minor`,
    }
  }

  if (kind === 'count-signature') {
    const slot = pickSlot(rng)
    const ring: 'major' | 'minor' = rng() < 0.5 ? 'major' : 'minor'
    // Default direction: match the slot's natural side (skip-and-redraw rule
    // per §9 #1). For signatureDelta === 0, direction is drawn 50/50 because
    // both are valid (zero-equivalence).
    let direction: 'sharp' | 'flat'
    if (slot.signatureDelta === 0) {
      direction = rng() < 0.5 ? 'sharp' : 'flat'
    } else if (slot.signatureDelta > 0) {
      // Sharp keys: 50/50 between sharp natural and flat alternate (if exists).
      const alt = getKeySignatureCount(slot, 'flat')
      direction = alt !== null && rng() < 0.5 ? 'flat' : 'sharp'
    } else {
      const alt = getKeySignatureCount(slot, 'sharp')
      direction = alt !== null && rng() < 0.5 ? 'sharp' : 'flat'
    }
    const acceptedCount = getKeySignatureCount(slot, direction) ?? 0
    const refName = ring === 'major' ? slot.canonical : slot.relativeMinor
    return {
      kind,
      referenceKey: { name: refName, ring },
      promptDirection: direction,
      acceptedCount,
      promptLabel: `${refName} ${ring}`,
    }
  }

  // kind === 'spell-enharmonic'
  const slot = pickEnharmonicSlot(rng)
  const ring: 'major' | 'minor' = rng() < 0.5 ? 'major' : 'minor'
  const direction: 'sharp' | 'flat' = rng() < 0.5 ? 'sharp' : 'flat'
  const acceptedSpellingRaw = getEnharmonicSpelling(slot, ring, direction)
  // Reference: show the OPPOSITE spelling so the question is non-trivial.
  const oppositeDirection: 'sharp' | 'flat' = direction === 'sharp' ? 'flat' : 'sharp'
  const refSpellingRaw = getEnharmonicSpelling(slot, ring, oppositeDirection) ?? slot.canonical
  // Strip 'm' suffix when storing as referenceKey.name — ring carries the mode.
  const refName = ring === 'minor' ? refSpellingRaw.replace(/m$/, '') : refSpellingRaw
  return {
    kind,
    referenceKey: { name: refName, ring },
    promptDirection: direction,
    acceptedSpellings: acceptedSpellingRaw ? [acceptedSpellingRaw] : [],
    promptLabel: `${refName} ${ring}`,
  }
}

export interface CofQuizVerdict {
  correct: boolean
  picked: CofQuizPicked
  question: CofQuizQuestion
}

/**
 * Switch on `q.kind`. Wheel/spelling answers expect `picked.kind === 'key'`;
 * signature answers expect `picked.kind === 'signature'`. Picked-kind mismatch
 * always returns `correct: false`. C-major signature is zero-equivalent
 * (`0♯ ≡ 0♭`) per §4.4.
 *
 * Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §4.4.
 */
export const validateCofQuizAnswer = (q: CofQuizQuestion, picked: CofQuizPicked): CofQuizVerdict => {
  switch (q.kind) {
    case 'find-relative':
    case 'find-closely-related':
    case 'find-parallel': {
      if (picked.kind !== 'key') {
        return { correct: false, picked, question: q }
      }
      const pickStr = picked.ring === 'minor' ? `${picked.name}m` : picked.name
      return { correct: q.acceptedAnswers.includes(pickStr), picked, question: q }
    }
    case 'spell-enharmonic': {
      if (picked.kind !== 'key') {
        return { correct: false, picked, question: q }
      }
      const pickStr = picked.ring === 'minor' ? `${picked.name}m` : picked.name
      return { correct: q.acceptedSpellings.includes(pickStr), picked, question: q }
    }
    case 'count-signature': {
      if (picked.kind !== 'signature') {
        return { correct: false, picked, question: q }
      }
      const countMatches = picked.count === q.acceptedCount
      const directionOk = picked.direction === q.promptDirection || picked.count === 0
      return { correct: countMatches && directionOk, picked, question: q }
    }
  }
}

export interface CofQuizResult {
  question: CofQuizQuestion
  verdict: CofQuizVerdict
}

export interface CofSessionStats {
  total: number
  correct: number
  byKind: Record<CofQuizQuestionKind, { total: number; correct: number }>
}

export const computeCofSessionStats = (results: readonly CofQuizResult[]): CofSessionStats => {
  const stats: CofSessionStats = {
    total: results.length,
    correct: results.filter(r => r.verdict.correct).length,
    byKind: {
      'find-relative': { total: 0, correct: 0 },
      'find-closely-related': { total: 0, correct: 0 },
      'find-parallel': { total: 0, correct: 0 },
      'count-signature': { total: 0, correct: 0 },
      'spell-enharmonic': { total: 0, correct: 0 },
    },
  }
  results.forEach(r => {
    const bucket = stats.byKind[r.question.kind]
    bucket.total += 1
    if (r.verdict.correct) {
      bucket.correct += 1
    }
  })
  return stats
}
